'use client'
import React, { useState } from 'react'

import { KeyRound, Mail, Phone } from 'lucide-react'

import GoogleIcon from '@/components/modules/google/components/GoogleIcon'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { glammaticApiRedirect } from '@/hooks/use-glammatic-api/glammatic-api'
import { cn } from '@/lib/html/class-name'

import {
  AUTH_METHODS,
  type AuthMethod,
  BUTTON_LABELS,
  BUTTON_SUBTITLES,
} from './LoginRedirectOptions.constants'

interface LoginRedirectOptionsProps {
  hidePasswordOption?: boolean
  hideEmailOption?: boolean
  hideSmsOption?: boolean
  hideGoogleOption?: boolean
}

export default function LoginRedirectOptions({
  hidePasswordOption = false,
  hideEmailOption = false,
  hideSmsOption = false,
  hideGoogleOption = false,
}: LoginRedirectOptionsProps) {
  const [loading, setLoading] = useState(false)

  function onAuthMethodClick(method: AuthMethod) {
    setLoading(true)
    const currentAppUrl = window.location.origin

    const pathTemplate = '/auth/login/{method}'
    const path = pathTemplate.replace('{method}', method)

    glammaticApiRedirect(path, { redirectUrl: currentAppUrl })

    setTimeout(() => {
      setLoading(false)
    }, 3000)
  }

  const primaryOptions = [
    {
      method: AUTH_METHODS.PASSWORD,
      hidden: hidePasswordOption,
      icon: KeyRound,
    },
    {
      method: AUTH_METHODS.EMAIL,
      hidden: hideEmailOption,
      icon: Mail,
    },
    {
      method: AUTH_METHODS.SMS,
      hidden: hideSmsOption,
      icon: Phone,
    },
  ].filter(({ hidden }) => !hidden)

  const showGoogleOption = !hideGoogleOption
  const showDivider = primaryOptions.length > 0 && showGoogleOption

  return (
    <div className="space-y-4">
      {primaryOptions.map(({ method, icon: Icon }) => {
        const subtitle = BUTTON_SUBTITLES[method]
        return (
          <Button
            key={method}
            type="button"
            variant="outline"
            onClick={() => onAuthMethodClick(method)}
            className={cn(
              'w-full h-auto py-3 px-3 flex items-center justify-start gap-4 mb-3',
              'hover:bg-accent/50 transition-all duration-200'
            )}
            disabled={loading}
          >
            <div className="flex items-center justify-center p-3 rounded-lg bg-base-text-light">
              <Icon className="text-foreground size-5" />
            </div>
            <div className="flex flex-col items-start text-left">
              <span className="text-base font-medium text-foreground">
                {BUTTON_LABELS[method]}
              </span>
              {subtitle && (
                <span className="text-sm text-placeholder mt-[1px]">{subtitle}</span>
              )}
            </div>
          </Button>
        )
      })}

      {showDivider && (
        <div className="relative my-5">
          <div className="absolute inset-0 flex items-center">
            <Separator />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="bg-background px-3 text-placeholder">OR</span>
          </div>
        </div>
      )}

      {showGoogleOption && (
        <Button
          type="button"
          variant="primary"
          onClick={() => onAuthMethodClick(AUTH_METHODS.GOOGLE)}
          className={cn('w-full h-12 transition-all duration-200', 'text-base')}
          disabled={loading}
        >
          <GoogleIcon width={18} height={18} color="currentColor" />
          <span className="ml-1">{BUTTON_LABELS[AUTH_METHODS.GOOGLE]}</span>
        </Button>
      )}
    </div>
  )
}
